/**
 * consentPolicyUrl.ts — WEB-2145 п.4.
 *
 * Какой адрес политики конфиденциальности показывает системный баннер
 * согласия (`components/consent/ConsentBanner.tsx`). До этой карточки баннер
 * ссылки не имел вовсе: посетителю сообщали про cookie и не давали прочитать,
 * что именно с ними делают.
 *
 * Лестница (первое найденное выигрывает) — порядок не косметический:
 *  1. авторский блок `cookie_consent` — его поле «URL политики cookie»
 *     (`cookiePolicyUrl`, ИИ-имя того же поля `policyUrl`,
 *     `components/editor/blockFieldNamePairs.ts`). Автор указал адрес именно
 *     про cookie — он точнее всех;
 *  2. блок согласия с условиями `terms_checkbox` — его `privacyUrl`. Это
 *     политика клиента, просто общая, а не про cookie;
 *  3. страница платформы `https://<хост кабинета>/privacy` (WEB-2330: наши
 *     юридические страницы живут в кабинете — `app.<апекс>`; корень апекса
 *     на бою зарезервирован под лендинг). Последняя ступень, потому
 *     что уводит посетителя С САЙТА КЛИЕНТА на наш: пока у клиента есть
 *     собственный адрес, отправлять туда нельзя.
 *
 * ⚠ Импорт типа `EditorScreen` — ТОЛЬКО типовой (`import type`): значенческий
 * импорт из `src/store/` запрещён в графе первого экрана публичной страницы
 * (правило WEB-2057, сторож публичного входа). Тот же приём — в соседнем
 * `cookieConsentBlock.ts`.
 */
import type { EditorScreen } from '@/store/editorSlice';
import { APP_HOST } from '@/config/public-host';
import { LEGAL_PRIVACY_PATH } from '@/constants/legalPaths';
import { isAllowedUrl } from '@/utils/isAllowedUrl';

/** Блок публичного содержимого в той мере, в какой он нужен лестнице. */
interface PolicyUrlBlockShape {
  readonly type?: string;
  /** cookie_consent, имя поля конструктора. */
  readonly cookiePolicyUrl?: unknown;
  /** cookie_consent, имя того же поля у ИИ-генератора. */
  readonly policyUrl?: unknown;
  /** terms_checkbox. */
  readonly privacyUrl?: unknown;
}

/**
 * Строка автора годится, только если после обрезки краёв в ней что-то есть:
 * поле, оставленное пустым, ступень не занимает — иначе лестница обрывалась бы
 * на пустоте и ссылка вела бы в никуда.
 */
function usableUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  // WEB-2385 п.1: до этой карточки схема принимала в поле политики любой
  // адрес, включая исполняемый `javascript:` — такие строки в базе есть.
  // Негодный адрес ступень НЕ занимает: лестница идёт дальше, а не отдаёт
  // баннеру согласия исполняемую ссылку. Годность спрашивается у общей двери
  // `isAllowedUrl`, второй копии политики схем здесь нет.
  return isAllowedUrl(trimmed) ? trimmed : undefined;
}

function blocksOf(
  screens: readonly EditorScreen[] | undefined,
): readonly PolicyUrlBlockShape[] {
  if (!Array.isArray(screens)) return [];
  const out: PolicyUrlBlockShape[] = [];
  for (const screen of screens) {
    const blocks = (screen as { blocks?: unknown }).blocks;
    if (!Array.isArray(blocks)) continue;
    for (const block of blocks) {
      if (block && typeof block === 'object') {
        out.push(block as PolicyUrlBlockShape);
      }
    }
  }
  return out;
}

/** Последняя ступень лестницы — страница политики самой платформы. */
export function platformPolicyUrl(host: string = APP_HOST): string {
  return `https://${host}${LEGAL_PRIVACY_PATH}`;
}

/**
 * Адрес политики для системного баннера согласия. `host` передаётся только
 * тестами; в бою берётся хост кабинета сборки (`APP_HOST`,
 * `config/public-host.ts`).
 */
export function resolveConsentPolicyUrl(
  screens: readonly EditorScreen[] | undefined,
  host: string = APP_HOST,
): string {
  const blocks = blocksOf(screens);

  for (const block of blocks) {
    if (block.type !== 'cookie_consent') continue;
    const own = usableUrl(block.cookiePolicyUrl) ?? usableUrl(block.policyUrl);
    if (own) return own;
  }

  for (const block of blocks) {
    if (block.type !== 'terms_checkbox') continue;
    const own = usableUrl(block.privacyUrl);
    if (own) return own;
  }

  return platformPolicyUrl(host);
}
